/**
 * Garment quality check.
 *
 * A check after the fact, not a gate: every garment here is already in the
 * member's wardrobe. The queue lists the ones that probably did not come out
 * right - processing failed or stalled, no title, no clean photo, details the
 * model was unsure of, or the member asked to discard it - so staff can fix,
 * re-run, hide or sign them off, many at a time.
 *
 * It replaced "Garment review & publishing", which treated review as a publish
 * gate and laid one batch out at a time. The old URL redirects here.
 */

import * as sdk from '@closet-theory/api';
import type {
  GarmentQualityCheckActionRequest,
  GarmentQualityCheckActionResult,
  GarmentQualityCheckEdit,
  GarmentQualityCheckItem,
  GarmentQualityCheckPage,
} from '@closet-theory/api';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { Badge } from '@ui/components/base/badges/badges';
import type { BadgeColors } from '@ui/components/base/badges/badge-types';
import { Button } from '@ui/components/base/buttons/button';
import { LoadingIndicator } from '@ui/components/application/loading-indicator/loading-indicator';
import { Callout, Section } from '@ui/console/primitives';
import { Bar, formatCount, plural } from '@ui/console/measures';
import { TextAreaField, TextField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { ActionDialog, Actions, DetailList, Page, Pager } from './components';
import { GARMENT_GRID_CLASS, when, words } from './consumer-insights';
import { EntityRef, MemberPicker } from './references';
import { GarmentImage, SignatureExpiryBoundary } from './garment-image';
import { STAGE_LABEL } from './wardrobe-import';
import { errorDetail } from './operation-policy';

const PAGE_SIZE = 50;
/** Bulk writes go in slices this size, so progress is real rather than a spinner. */
const ACTION_CHUNK = 25;

export type QualityReason = GarmentQualityCheckItem['reasons'][number];
export type QualityAction = GarmentQualityCheckActionRequest['action'];

/** The order chips are shown in: the most serious first. */
export const QUALITY_REASONS: readonly QualityReason[] = [
  'ingestion_failed',
  'ingestion_stuck',
  'missing_canonical_image',
  'member_flagged',
  'missing_title',
  'placeholder_title',
  'missing_description',
  'low_confidence',
  'recently_ingested',
];

export const REASON_LABEL: Record<QualityReason, string> = {
  ingestion_failed: 'Failed to process',
  ingestion_stuck: 'Stuck processing',
  missing_title: 'No title',
  placeholder_title: 'Placeholder title',
  missing_description: 'No description',
  missing_canonical_image: 'No clean photo',
  low_confidence: 'Details may be wrong',
  member_flagged: 'Member asked to remove',
  recently_ingested: 'Added in the last day',
};

const REASON_MEANING: Record<QualityReason, string> = {
  ingestion_failed: 'Our processing gave up on this garment. Re-running it usually fixes this.',
  ingestion_stuck: 'Processing hasn’t finished, or never started, more than an hour after upload.',
  missing_title: 'The garment has no name, so the member sees it without one.',
  placeholder_title: 'The garment is still called “Untitled garment”.',
  missing_description: 'The garment has no written description.',
  missing_canonical_image: 'Processing finished but did not produce the clean product-style photo.',
  low_confidence: 'Our processing was unsure about what this garment is. Check its details.',
  member_flagged: 'The member asked for this garment to be discarded.',
  recently_ingested: 'Added in the last 24 hours. Shown only when you pick this filter.',
};

const REASON_COLOR: Record<QualityReason, BadgeColors> = {
  ingestion_failed: 'error',
  ingestion_stuck: 'warning',
  missing_canonical_image: 'warning',
  member_flagged: 'purple',
  missing_title: 'gray',
  placeholder_title: 'gray',
  missing_description: 'gray',
  low_confidence: 'warning',
  recently_ingested: 'blue',
};

const ACTION_LABEL: Record<QualityAction, string> = {
  rerun: 'Re-run processing',
  hide: 'Hide',
  mark_reviewed: 'Mark as checked',
};

const ACTION_CONSEQUENCE: Record<QualityAction, string> = {
  rerun:
    'Processes the member’s original photos again from the start. The garment stays in their wardrobe while it runs, and any title, category or details you corrected are kept.',
  hide: 'Removes the garment from the member’s wardrobe and from this queue. Use this for duplicates, photos that are not clothes, or anything the member asked to remove.',
  mark_reviewed:
    'Says you have looked and it is fine. It leaves this queue and only comes back if something new goes wrong with it.',
};

const ACTION_REASON: Record<QualityAction, string> = {
  rerun: 'Re-ran processing from the admin garment quality check.',
  hide: 'Hid the garment from the admin garment quality check.',
  mark_reviewed: 'Marked as checked from the admin garment quality check.',
};

const ACTION_DONE: Record<QualityAction, string> = {
  rerun: 'Processing restarted',
  hide: 'Hidden',
  mark_reviewed: 'Marked as checked',
};

const EDIT_REASON = 'Corrected garment details from the admin garment quality check.';

/** What each per-garment failure means, for someone who does not read codes. */
const ERROR_MEANING: Record<string, string> = {
  not_found: 'This garment no longer exists, or someone already removed it.',
  missing_source_image:
    'The member’s original photo is missing, so there is nothing to process again.',
  action_failed: 'Something went wrong on our side. Try again in a minute.',
  request_failed: 'We could not reach the server for this one. Try again in a minute.',
  not_available: 'This action will be available after the next backend update.',
};

export function explainActionError(code: string | null | undefined): string {
  return (code && ERROR_MEANING[code]) || 'This one did not go through. Try again in a minute.';
}

type IngestionStatus = GarmentQualityCheckItem['ingestion']['status'];

const INGESTION_WORDS: Record<IngestionStatus, string> = {
  queued: 'Waiting to be processed',
  running: 'Being processed now',
  retrying: 'Failed, retrying',
  failed: 'Processing failed',
  done: 'Processed',
};

const INGESTION_COLOR: Record<IngestionStatus, BadgeColors> = {
  queued: 'gray',
  running: 'blue',
  retrying: 'warning',
  failed: 'error',
  done: 'success',
};

/** Processing has not settled yet, so the clean photo may still be coming. */
const INGESTION_IN_FLIGHT: ReadonlySet<IngestionStatus> = new Set([
  'queued',
  'running',
  'retrying',
]);

const STEP_WORDS: Record<string, string> = {
  dedup_grouping: 'grouping photos of the same garment',
  deduplication: 'checking for duplicates',
  attribute_extraction: 'reading the garment’s details',
  enhancement: 'making the clean photo',
  validation: 'checking the clean photo',
  reverse_search: 'looking up the product online',
  title_generation: 'writing the title and description',
  embedding: 'preparing it for outfit suggestions',
};

function stepWords(step: string): string {
  return STEP_WORDS[step] ?? (STAGE_LABEL[step] ?? words(step)).toLowerCase();
}

export const KEY_ATTRIBUTES = ['colour', 'pattern', 'material', 'fit', 'occasion'] as const;
type KeyAttribute = (typeof KEY_ATTRIBUTES)[number];

const ATTRIBUTE_LABEL: Record<KeyAttribute, string> = {
  colour: 'Colour',
  pattern: 'Pattern',
  material: 'Material',
  fit: 'Fit',
  occasion: 'Occasion',
};

function attributeText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((entry) => attributeText(entry)).join(', ');
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function garmentTitle(item: GarmentQualityCheckItem): string {
  return item.title?.trim() || 'Untitled';
}

function categoryText(item: GarmentQualityCheckItem): string {
  return [item.category, item.subcategory]
    .filter((part): part is string => Boolean(part?.trim()))
    .map(words)
    .join(' · ');
}

/* --------------------------------------------------------------- operations */

type Read<T> = { data?: T | undefined; error?: unknown; response?: { status: number } };
type ListOperation = (options: {
  query: Record<string, unknown>;
}) => Promise<Read<GarmentQualityCheckPage>>;
type ActionOperation = (options: {
  body: GarmentQualityCheckActionRequest;
}) => Promise<Read<{ results: GarmentQualityCheckActionResult[] }>>;
type EditOperation = (options: {
  path: { id: string; garment_id: string };
  body: GarmentQualityCheckEdit;
}) => Promise<Read<GarmentQualityCheckItem>>;

const OPERATIONS = {
  list: 'listGarmentQualityCheckApiV1InternalWardrobeQualityCheckGet',
  act: 'applyGarmentQualityCheckActionsApiV1InternalWardrobeQualityCheckActionsPost',
  edit: 'editGarmentQualityOverrideApiV1InternalUsersIdWardrobeGarmentsGarmentIdPatch',
} as const;

/**
 * The operation from the generated client, when this build has it. Production
 * can run an older API than the console, so a missing operation is a state the
 * page renders, not a crash.
 */
function operation<T>(name: string): T | undefined {
  try {
    const found = (sdk as unknown as Record<string, unknown>)[name];
    return typeof found === 'function' ? (found as T) : undefined;
  } catch {
    return undefined;
  }
}

/** The server answering that the route is not there: not deployed yet. */
const NOT_DEPLOYED: ReadonlySet<number> = new Set([404, 405, 501]);

function statusOf(result: Read<unknown>): number {
  return result.response?.status ?? (result.error ? 500 : 200);
}

/** The offset of the last page that has anything on it. */
export function lastOffset(total: number, limit: number): number {
  if (total <= 0 || limit <= 0) return 0;
  return Math.floor((total - 1) / limit) * limit;
}

/* ------------------------------------------------------------------- filters */

type Filters = {
  reason: QualityReason | '';
  member: string;
  category: string;
  since: string;
  until: string;
  offset: number;
};

function readFilters(search: URLSearchParams): Filters {
  const reason = search.get('reason') ?? '';
  return {
    reason: (QUALITY_REASONS as readonly string[]).includes(reason)
      ? (reason as QualityReason)
      : '',
    member: search.get('member') ?? '',
    category: search.get('category') ?? '',
    since: search.get('since') ?? '',
    until: search.get('until') ?? '',
    offset: Math.max(0, Number(search.get('offset') ?? 0) || 0),
  };
}

type PageState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'forbidden' }
  | { status: 'failed'; detail: string }
  | { status: 'ready'; data: GarmentQualityCheckPage; receivedAt: number };

/* ---------------------------------------------------------------- components */

function ReasonBadge({ reason }: { reason: QualityReason }) {
  return (
    <Badge type="pill-color" size="sm" color={REASON_COLOR[reason] ?? 'gray'}>
      {REASON_LABEL[reason] ?? words(reason)}
    </Badge>
  );
}

function ReasonChips({
  counts,
  total,
  selected,
  onSelect,
}: {
  counts: Partial<Record<string, number>> | undefined;
  total: number | undefined;
  selected: QualityReason | '';
  onSelect: (reason: QualityReason | '') => void;
}) {
  const chip = (value: QualityReason | '', label: string, count: number | undefined) => {
    const active = selected === value;
    return (
      <li key={value || 'all'}>
        <button
          type="button"
          aria-pressed={active}
          onClick={() => onSelect(active ? '' : value)}
          className={
            active
              ? 'rounded-full border border-brand bg-brand-primary px-3 py-1.5 text-sm font-medium text-brand-secondary outline-brand focus-visible:outline-2'
              : 'rounded-full border border-secondary bg-primary px-3 py-1.5 text-sm font-medium text-secondary outline-brand hover:bg-primary_hover focus-visible:outline-2'
          }
        >
          {count === undefined ? label : `${label} (${formatCount(count)})`}
        </button>
      </li>
    );
  };
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Filter by problem">
      {chip('', 'Everything', selected ? undefined : total)}
      {QUALITY_REASONS.map((reason) => {
        const count = counts?.[reason];
        // "Added in the last day" is counted only while it is the filter.
        if (reason === 'recently_ingested') return chip(reason, REASON_LABEL[reason], count);
        return chip(reason, REASON_LABEL[reason], count ?? 0);
      })}
    </ul>
  );
}

function GarmentCard({
  item,
  selected,
  onToggle,
  onOpen,
}: {
  item: GarmentQualityCheckItem;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const title = garmentTitle(item);
  return (
    <li
      className={
        selected
          ? 'flex min-w-0 flex-col gap-2 rounded-xl border border-brand bg-brand-primary p-3'
          : 'flex min-w-0 flex-col gap-2 rounded-xl border border-secondary bg-primary p-3'
      }
    >
      <label className="flex items-center gap-2 text-xs text-tertiary">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${title}`}
          className="size-4 cursor-pointer rounded accent-brand-solid outline-brand focus-visible:outline-2"
        />
        Select
      </label>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={`Open ${title}`}
        onClick={onOpen}
        className="flex min-w-0 flex-col gap-2 text-left outline-brand focus-visible:outline-2"
      >
        <GarmentImage
          url={item.thumbnail_url}
          originalUrl={item.original_image_urls?.[0]}
          pending={INGESTION_IN_FLIGHT.has(item.ingestion.status)}
          alt={title}
        />
        <span
          className={
            item.title?.trim()
              ? 'truncate text-sm font-medium text-primary'
              : 'truncate text-sm font-medium text-tertiary italic'
          }
          title={title}
        >
          {title}
        </span>
        <span className="truncate text-xs text-tertiary">{categoryText(item)}</span>
      </button>
      <EntityRef kind="member" id={item.member.id} label={item.member.name} hideId />
      <span className="flex flex-wrap gap-1">
        {item.reasons.map((reason) => (
          <ReasonBadge key={reason} reason={reason} />
        ))}
      </span>
    </li>
  );
}

/** Commits on Enter or on leaving the field, so typing does not re-read per key. */
function CommitField({
  label,
  value,
  onCommit,
  type = 'text',
  hint,
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  type?: 'text' | 'date';
  hint?: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const next = draft.trim();
    if (next !== value) onCommit(next);
  };
  return (
    <TextField
      label={label}
      type={type}
      value={draft}
      {...(hint ? { hint } : {})}
      onChange={(event) => {
        setDraft(event.currentTarget.value);
        if (type === 'date') onCommit(event.currentTarget.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
      }}
    />
  );
}

/* ------------------------------------------------------------- bulk actions */

type ActionRun = {
  action: QualityAction;
  garments: GarmentQualityCheckItem[];
  phase: 'confirm' | 'running' | 'done';
  done: number;
  results: GarmentQualityCheckActionResult[];
};

function ActionDialogFlow({
  run,
  onRun,
  onClose,
}: {
  run: ActionRun;
  onRun: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const count = run.garments.length;
  const noun = plural(count, 'garment');
  const label = ACTION_LABEL[run.action];
  const byId = new Map(run.garments.map((garment) => [garment.garment_id, garment]));
  const failed = run.results.filter((result) => !result.ok);
  const succeeded = run.results.length - failed.length;

  return (
    <ActionDialog
      title={`${label}: ${formatCount(count)} ${noun}`}
      description={ACTION_CONSEQUENCE[run.action]}
      onClose={run.phase === 'running' ? () => undefined : onClose}
      footer={
        run.phase === 'confirm' ? (
          <>
            <Button size="sm" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              color={run.action === 'hide' ? 'primary-destructive' : 'primary'}
              onClick={() => onRun(note)}
            >
              {`Yes, ${label.toLowerCase()}`}
            </Button>
          </>
        ) : run.phase === 'done' ? (
          <Button size="sm" color="primary" onClick={onClose}>
            Close
          </Button>
        ) : null
      }
    >
      {run.phase === 'confirm' ? (
        <div className="flex flex-col gap-3">
          <ul className="flex flex-wrap gap-1.5 text-sm text-secondary">
            {run.garments.slice(0, 12).map((garment) => (
              <li key={garment.garment_id} className="rounded-md bg-secondary px-2 py-0.5">
                {garmentTitle(garment)}
              </li>
            ))}
            {count > 12 ? (
              <li className="px-2 py-0.5 text-tertiary">and {formatCount(count - 12)} more</li>
            ) : null}
          </ul>
          <TextAreaField
            label="Note (optional)"
            hint="Saved in the audit log with this change."
            value={note}
            rows={2}
            onChange={(event) => setNote(event.currentTarget.value)}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5" aria-live="polite">
            <span className="flex items-center gap-2 text-sm text-secondary">
              {run.phase === 'running' ? <LoadingIndicator size="sm" type="dot-circle" /> : null}
              {run.phase === 'running'
                ? `Working… ${formatCount(run.done)} of ${formatCount(count)} done`
                : failed.length
                  ? `${formatCount(succeeded)} of ${formatCount(count)} done. ${formatCount(failed.length)} did not go through.`
                  : `All ${formatCount(count)} done.`}
            </span>
            <Bar
              fraction={count ? run.done / count : 0}
              tone={failed.length ? 'warning' : 'success'}
              label={`Progress: ${run.done} of ${count} garments`}
            />
          </div>
          {run.results.length ? (
            <ul
              aria-label="Results"
              className="flex max-h-80 flex-col divide-y divide-secondary overflow-auto rounded-xl border border-secondary"
            >
              {run.results.map((result) => {
                const garment = byId.get(result.garment_id);
                return (
                  <li
                    key={result.garment_id}
                    className="flex flex-col gap-0.5 px-3 py-2"
                    data-ok={result.ok}
                  >
                    <span className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-primary">
                        {garment ? garmentTitle(garment) : 'Garment'}
                      </span>
                      <Badge type="pill-color" size="sm" color={result.ok ? 'success' : 'error'}>
                        {result.ok ? ACTION_DONE[run.action] : 'Did not work'}
                      </Badge>
                    </span>
                    {result.ok ? null : (
                      <span className="text-xs text-error-primary">
                        {explainActionError(result.error_code)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      )}
    </ActionDialog>
  );
}

/* -------------------------------------------------------------- detail drawer */

type EditDraft = {
  title: string;
  description: string;
  category: string;
  subcategory: string;
} & Record<KeyAttribute, string>;

function draftFrom(item: GarmentQualityCheckItem): EditDraft {
  const attributes = item.key_attributes ?? {};
  return {
    title: item.title ?? '',
    description: item.description ?? '',
    category: item.category ?? '',
    subcategory: item.subcategory ?? '',
    colour: attributeText(attributes['colour']),
    pattern: attributeText(attributes['pattern']),
    material: attributeText(attributes['material']),
    fit: attributeText(attributes['fit']),
    occasion: attributeText(attributes['occasion']),
  };
}

/** Only what changed, so a save never overwrites a field nobody touched. */
export function editBody(
  item: GarmentQualityCheckItem,
  draft: EditDraft,
): GarmentQualityCheckEdit | undefined {
  const before = draftFrom(item);
  const body: GarmentQualityCheckEdit = { reason: EDIT_REASON };
  let changed = false;
  const title = draft.title.trim();
  if (title !== before.title.trim()) {
    body.title = title || null;
    changed = true;
  }
  const description = draft.description.trim();
  if (description !== before.description.trim()) {
    body.description = description || null;
    changed = true;
  }
  const category = draft.category.trim();
  if (category && category !== before.category) {
    body.category = category;
    changed = true;
  }
  const subcategory = draft.subcategory.trim();
  if (subcategory !== before.subcategory.trim()) {
    body.subcategory = subcategory || null;
    changed = true;
  }
  for (const key of KEY_ATTRIBUTES) {
    const next = draft[key].trim();
    if (next === before[key].trim()) continue;
    const original = item.key_attributes?.[key];
    body[key] = !next
      ? null
      : Array.isArray(original)
        ? next
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean)
        : next;
    changed = true;
  }
  return changed ? body : undefined;
}

function GarmentDrawer({
  item,
  signedAt,
  onClose,
  onSaved,
  onAction,
  onRefresh,
}: {
  item: GarmentQualityCheckItem;
  signedAt: number | undefined;
  onClose: () => void;
  onSaved: (item: GarmentQualityCheckItem) => void;
  onAction: (action: QualityAction) => void;
  onRefresh: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<EditDraft>(() => draftFrom(item));
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<
    { kind: 'saved' } | { kind: 'unchanged' } | { kind: 'failed'; detail: string }
  >();
  const title = garmentTitle(item);
  const originals = item.original_image_urls ?? [];
  const set = (key: keyof EditDraft) => (event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value;
    setDraft((current) => ({ ...current, [key]: value }));
    setOutcome(undefined);
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = editBody(item, draft);
    if (!body) {
      setOutcome({ kind: 'unchanged' });
      return;
    }
    const edit = operation<EditOperation>(OPERATIONS.edit);
    if (!edit) {
      setOutcome({ kind: 'failed', detail: ERROR_MEANING['not_available']! });
      return;
    }
    setSaving(true);
    configureAdminApi();
    const result = await edit({
      path: { id: item.member.id, garment_id: item.garment_id },
      body,
    }).catch(() => undefined);
    setSaving(false);
    if (!result) {
      setOutcome({ kind: 'failed', detail: ERROR_MEANING['request_failed']! });
      return;
    }
    const status = statusOf(result);
    handleUnauthorized(status);
    if (NOT_DEPLOYED.has(status) && status !== 404) {
      setOutcome({ kind: 'failed', detail: ERROR_MEANING['not_available']! });
      return;
    }
    if (result.error || !result.data || status >= 400) {
      setOutcome({
        kind: 'failed',
        detail:
          status === 404
            ? ERROR_MEANING['not_found']!
            : status === 403
              ? 'Your role is not allowed to change garment details.'
              : errorDetail(result.error, 'The changes were not saved. Try again in a minute.'),
      });
      return;
    }
    setOutcome({ kind: 'saved' });
    setDraft(draftFrom(result.data));
    onSaved(result.data);
  };

  return (
    <ActionDialog
      title={title}
      description="Compare the clean photo with what the member uploaded, see how processing went, and correct anything that is wrong."
      width="lg"
      onClose={onClose}
      footer={
        <>
          {(['rerun', 'hide', 'mark_reviewed'] as const).map((action) => (
            <Button
              key={action}
              size="sm"
              color={action === 'mark_reviewed' ? 'primary' : 'secondary'}
              onClick={() => onAction(action)}
            >
              {ACTION_LABEL[action]}
            </Button>
          ))}
        </>
      }
    >
      <SignatureExpiryBoundary signedAt={signedAt} onRefresh={onRefresh}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <figure className="flex flex-col gap-1.5">
            <figcaption className="text-xs font-medium text-tertiary">Clean photo</figcaption>
            <GarmentImage
              url={item.canonical_image_url}
              alt={`${title} — clean photo`}
              absentTitle="No clean photo yet"
              absentDetail={
                item.ingestion.status === 'done'
                  ? 'Processing finished without making the clean photo. Re-running processing usually makes one.'
                  : 'The clean photo is made during processing, which has not finished for this garment.'
              }
              onRefresh={onRefresh}
            />
          </figure>
          <figure className="flex flex-col gap-1.5">
            <figcaption className="text-xs font-medium text-tertiary">
              What the member uploaded
            </figcaption>
            {originals.length ? (
              <ul className="grid grid-cols-2 gap-2">
                {originals.map((url, index) => (
                  <li key={url}>
                    <GarmentImage
                      url={url}
                      alt={`${title} — upload ${index + 1} of ${originals.length}`}
                      onRefresh={onRefresh}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-sm text-tertiary">No original photo is on record.</span>
            )}
          </figure>
        </div>
      </SignatureExpiryBoundary>

      <Section title="Why it is here">
        <ul className="flex flex-col gap-2">
          {item.reasons.map((reason) => (
            <li key={reason} className="flex flex-col items-start gap-0.5">
              <ReasonBadge reason={reason} />
              <span className="text-sm text-tertiary">{REASON_MEANING[reason]}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Processing">
        <DetailList
          items={[
            [
              'Where it is',
              <Badge
                type="pill-color"
                size="sm"
                color={INGESTION_COLOR[item.ingestion.status] ?? 'gray'}
              >
                {INGESTION_WORDS[item.ingestion.status] ?? words(item.ingestion.status)}
              </Badge>,
            ],
            ...(item.ingestion.job_id
              ? ([
                  ['Processing job', <EntityRef kind="workflow_job" id={item.ingestion.job_id} />],
                ] as Array<[ReactNode, ReactNode]>)
              : []),
            ...(item.ingestion.failing_step
              ? ([['Stopped while', stepWords(item.ingestion.failing_step)]] as Array<
                  [ReactNode, ReactNode]
                >)
              : []),
            ['Member', <EntityRef kind="member" id={item.member.id} label={item.member.name} />],
            ['Added', when(item.created_at)],
            [
              'Last checked',
              item.reviewed_at && item.reviewed_by ? (
                <span className="flex flex-wrap items-center gap-x-2">
                  {when(item.reviewed_at)}
                  <span className="text-tertiary">by</span>
                  <EntityRef kind="staff" id={item.reviewed_by} hideId />
                </span>
              ) : (
                when(item.reviewed_at, 'Not checked yet')
              ),
            ],
          ]}
        />
        {item.ingestion.error ? (
          <details className="rounded-lg border border-secondary bg-secondary px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium text-secondary">Details</summary>
            <p className="pt-2 font-mono text-xs break-words whitespace-pre-wrap text-tertiary">
              {item.ingestion.error}
            </p>
          </details>
        ) : null}
      </Section>

      <form className="flex flex-col gap-3" onSubmit={(event) => void save(event)}>
        <Section
          title="Correct the details"
          description="What you change here sticks: re-running processing keeps your corrections."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField label="Title" value={draft.title} onChange={set('title')} />
            <TextField label="Category" value={draft.category} onChange={set('category')} />
            <TextField
              label="Subcategory"
              value={draft.subcategory}
              onChange={set('subcategory')}
            />
            {KEY_ATTRIBUTES.map((key) => (
              <TextField
                key={key}
                label={ATTRIBUTE_LABEL[key]}
                value={draft[key]}
                onChange={set(key)}
              />
            ))}
          </div>
          <TextAreaField
            label="Description"
            hint="Clear it to remove the description."
            value={draft.description}
            rows={3}
            onChange={set('description')}
          />
          {outcome?.kind === 'saved' ? (
            <Callout tone="success" title="Saved. The member sees the corrected details now." />
          ) : outcome?.kind === 'unchanged' ? (
            <Callout tone="neutral" title="Nothing has changed yet, so there is nothing to save." />
          ) : outcome?.kind === 'failed' ? (
            <Callout tone="error" title="The changes were not saved.">
              {outcome.detail}
            </Callout>
          ) : null}
          <Actions>
            <Button size="sm" color="primary" type="submit" isDisabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Actions>
        </Section>
      </form>
    </ActionDialog>
  );
}

/* ---------------------------------------------------------------------- page */

export function GarmentQualityCheck() {
  const [search, setSearch] = useSearchParams();
  const filters = readFilters(search);
  const { reason, member, category, since, until, offset } = filters;
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string>();
  const [run, setRun] = useState<ActionRun>();

  const load = useCallback(
    async (options?: { preserve?: boolean }) => {
      const list = operation<ListOperation>(OPERATIONS.list);
      if (!list) {
        setState({ status: 'unavailable' });
        return;
      }
      if (options?.preserve) setRefreshing(true);
      else setState({ status: 'loading' });
      configureAdminApi();
      const result = await list({
        query: {
          limit: PAGE_SIZE,
          offset,
          ...(reason ? { reason } : {}),
          ...(member ? { member_id: member } : {}),
          ...(category ? { category } : {}),
          ...(since ? { since } : {}),
          ...(until ? { until } : {}),
        },
      }).catch(() => undefined);
      setRefreshing(false);
      if (!result) {
        setState({ status: 'failed', detail: 'The quality check queue could not be reached.' });
        return;
      }
      const status = statusOf(result);
      handleUnauthorized(status);
      if (NOT_DEPLOYED.has(status)) setState({ status: 'unavailable' });
      else if (status === 403) setState({ status: 'forbidden' });
      else if (result.error || !result.data || status >= 400)
        setState({
          status: 'failed',
          detail:
            status === 422
              ? errorDetail(result.error, 'One of the filters is not valid.')
              : 'The quality check queue could not be loaded. Try again in a minute.',
        });
      else setState({ status: 'ready', data: result.data, receivedAt: Date.now() });
    },
    [category, member, offset, reason, since, until],
  );

  useEffect(() => {
    setSelected(new Set());
    void load();
  }, [load]);

  const write = (patch: Partial<Record<keyof Filters, string>>) =>
    setSearch((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(patch)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      if (!('offset' in patch)) next.delete('offset');
      return next;
    });

  const page = state.status === 'ready' ? state.data : undefined;
  const items = page?.items ?? [];
  const opened = items.find((item) => item.garment_id === openId);
  const chosen = items.filter((item) => selected.has(item.garment_id));
  const narrowed = Boolean(reason || member || category || since || until);

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const replaceItem = (updated: GarmentQualityCheckItem) =>
    setState((current) =>
      current.status === 'ready'
        ? {
            ...current,
            data: {
              ...current.data,
              items: current.data.items.map((item) =>
                item.garment_id === updated.garment_id ? updated : item,
              ),
            },
          }
        : current,
    );

  const startRun = (action: QualityAction, garments: GarmentQualityCheckItem[]) => {
    if (!garments.length) return;
    setOpenId(undefined);
    setRun({ action, garments, phase: 'confirm', done: 0, results: [] });
  };

  const execute = async (note: string) => {
    if (!run) return;
    const { action, garments } = run;
    const reasonText = note.trim()
      ? `${ACTION_REASON[action]} Note: ${note.trim()}`
      : ACTION_REASON[action];
    const act = operation<ActionOperation>(OPERATIONS.act);
    setRun({ ...run, phase: 'running', done: 0, results: [] });
    const results: GarmentQualityCheckActionResult[] = [];
    configureAdminApi();
    for (let start = 0; start < garments.length; start += ACTION_CHUNK) {
      const ids = garments.slice(start, start + ACTION_CHUNK).map((garment) => garment.garment_id);
      const result = act
        ? await act({ body: { action, garment_ids: ids, reason: reasonText } }).catch(
            () => undefined,
          )
        : undefined;
      const status = result ? statusOf(result) : undefined;
      if (status !== undefined) handleUnauthorized(status);
      const answered = new Map(
        (result && !result.error && status !== undefined && status < 400
          ? (result.data?.results ?? [])
          : []
        ).map((row) => [row.garment_id, row]),
      );
      const fallback =
        !act || (status !== undefined && NOT_DEPLOYED.has(status))
          ? 'not_available'
          : 'request_failed';
      for (const id of ids) {
        results.push(answered.get(id) ?? { garment_id: id, ok: false, error_code: fallback });
      }
      setRun((current) =>
        current ? { ...current, done: results.length, results: [...results] } : current,
      );
    }
    setRun((current) => (current ? { ...current, phase: 'done' } : current));
    setSelected(new Set(results.filter((result) => !result.ok).map((result) => result.garment_id)));
  };

  const closeRun = () => {
    const finished = run?.phase === 'done';
    setRun(undefined);
    if (finished) void load({ preserve: true });
  };

  const allOnPage = items.length > 0 && items.every((item) => selected.has(item.garment_id));
  // A bulk action can empty the last page while garments remain earlier on.
  const pastEnd = Boolean(page && items.length === 0 && page.total > 0 && page.offset > 0);
  const lastPageOffset = page ? lastOffset(page.total, page.limit) : 0;
  useEffect(() => {
    if (pastEnd) write({ offset: lastPageOffset ? String(lastPageOffset) : '' });
    // `write` is rebuilt every render; stepping back depends only on these.
  }, [pastEnd, lastPageOffset]);

  return (
    <Page
      title="Garment quality check"
      purpose="Use this page to find garments that did not come out right after processing — a failed photo, a missing title, details that look wrong — and fix them. Members already have these garments; this is a check after the fact, not a gate."
      toolbar={{
        filters: (
          <>
            <MemberPicker
              value={member}
              onChange={(id) => write({ member: id })}
              hint="Only this member’s garments."
            />
            <CommitField
              label="Category"
              value={category}
              onCommit={(value) => write({ category: value })}
              hint="For example: shirt, jeans, dress."
            />
            <CommitField
              label="Added from"
              type="date"
              value={since}
              onCommit={(value) => write({ since: value })}
            />
            <CommitField
              label="Added until"
              type="date"
              value={until}
              onCommit={(value) => write({ until: value })}
            />
          </>
        ),
        onClear: () => setSearch(new URLSearchParams()),
        canClear: narrowed,
        onRefresh: () => void load({ preserve: true }),
        refreshing,
      }}
    >
      {state.status === 'unavailable' ? (
        <Callout tone="info" title="Quality check will be available after the next backend update.">
          Nothing is wrong. This page needs a newer version of our server, which has not gone out
          yet. Once it has, garments that need a second look will show up here on their own.
        </Callout>
      ) : state.status === 'forbidden' ? (
        <Callout tone="warning" title="Your role cannot open the garment quality check.">
          You are still signed in. Ask an admin if you need access.
        </Callout>
      ) : state.status === 'failed' ? (
        <Callout
          tone="error"
          title={state.detail}
          actions={
            <Button size="sm" color="secondary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : (
        <>
          <Section
            title="What needs a look"
            description="Pick a problem to see only those garments. A garment with more than one problem is counted under each."
          >
            <ReasonChips
              counts={page?.reason_counts}
              total={page?.total}
              selected={reason}
              onSelect={(value) => write({ reason: value })}
            />
            {reason && page ? (
              <p className="text-sm text-tertiary">
                {REASON_MEANING[reason]} The other counts show how many of these also have that
                problem.
              </p>
            ) : null}
          </Section>

          {state.status === 'loading' ? (
            <div className="flex items-center gap-2 py-8 text-sm text-tertiary" aria-live="polite">
              <LoadingIndicator size="sm" type="dot-circle" />
              Loading…
            </div>
          ) : pastEnd ? (
            <Callout
              tone="info"
              title={`This page is past the end. ${formatCount(page!.total)} ${plural(page!.total, 'garment')} still to check on earlier pages.`}
            >
              <Button
                size="sm"
                color="secondary"
                onClick={() => write({ offset: lastPageOffset ? String(lastPageOffset) : '' })}
              >
                Go to the last page
              </Button>
            </Callout>
          ) : items.length === 0 ? (
            <Callout
              tone="success"
              title={
                narrowed ? 'No garment matches these filters.' : 'Nothing needs a look right now.'
              }
            >
              {narrowed
                ? 'Clear the filters to see every garment that needs checking.'
                : 'Every garment has either come out fine or been checked already.'}
            </Callout>
          ) : (
            <Section
              title={`${formatCount(page!.total)} ${plural(page!.total, 'garment')} to check`}
            >
              <div
                role="toolbar"
                aria-label="Selected garments"
                className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-secondary bg-primary p-3 shadow-xs"
              >
                <label className="flex items-center gap-2 text-sm text-secondary">
                  <input
                    type="checkbox"
                    checked={allOnPage}
                    onChange={() =>
                      setSelected(
                        allOnPage ? new Set() : new Set(items.map((item) => item.garment_id)),
                      )
                    }
                    className="size-4 cursor-pointer rounded accent-brand-solid outline-brand focus-visible:outline-2"
                  />
                  Select all on this page
                </label>
                <span className="text-sm text-tertiary">
                  {chosen.length
                    ? `${formatCount(chosen.length)} selected`
                    : 'Select garments to act on several at once.'}
                </span>
                <span className="ml-auto flex flex-wrap gap-2">
                  {(['rerun', 'hide', 'mark_reviewed'] as const).map((action) => (
                    <Button
                      key={action}
                      size="sm"
                      color={action === 'mark_reviewed' ? 'primary' : 'secondary'}
                      isDisabled={!chosen.length}
                      onClick={() => startRun(action, chosen)}
                    >
                      {ACTION_LABEL[action]}
                    </Button>
                  ))}
                </span>
              </div>
              <SignatureExpiryBoundary
                signedAt={state.status === 'ready' ? state.receivedAt : undefined}
                onRefresh={() => load({ preserve: true })}
              >
                <ul className={GARMENT_GRID_CLASS} aria-label="Garments to check">
                  {items.map((item) => (
                    <GarmentCard
                      key={item.garment_id}
                      item={item}
                      selected={selected.has(item.garment_id)}
                      onToggle={() => toggle(item.garment_id)}
                      onOpen={() => setOpenId(item.garment_id)}
                    />
                  ))}
                </ul>
              </SignatureExpiryBoundary>
              {page!.total > page!.limit ? (
                <Pager
                  offset={page!.offset}
                  limit={page!.limit}
                  total={page!.total}
                  onPage={(next) => write({ offset: next ? String(next) : '' })}
                />
              ) : null}
            </Section>
          )}
        </>
      )}
      {opened ? (
        <GarmentDrawer
          key={opened.garment_id}
          item={opened}
          signedAt={state.status === 'ready' ? state.receivedAt : undefined}
          onClose={() => setOpenId(undefined)}
          onSaved={replaceItem}
          onAction={(action) => startRun(action, [opened])}
          onRefresh={() => load({ preserve: true })}
        />
      ) : null}
      {run ? (
        <ActionDialogFlow run={run} onRun={(note) => void execute(note)} onClose={closeRun} />
      ) : null}
    </Page>
  );
}
